import {
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	Grid,
	MenuItem,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import MainGrid from '../components/MainGrid';
import { GroupAddOutlined, PersonAddOutlined } from '@mui/icons-material';
import { dark, teal } from '../assets/theme/Colors';

const users = [
	{
		name: 'Admin Kumar',
		initials: 'AK',
		role: 'Care Home Admin',
		lastActive: 'Now',
		status: 'Active',
		color: '#0f766e',
		bg: '#ccfbf1',
	},
	{
		name: 'Maria Johnson',
		initials: 'MJ',
		role: 'Senior Nurse',
		lastActive: '10 min ago',
		status: 'Active',
		color: '#2563eb',
		bg: '#dbeafe',
	},
	{
		name: 'Tom Williams',
		initials: 'TW',
		role: 'Care Staff',
		lastActive: '2 hrs ago',
		status: 'Active',
		color: '#7c3aed',
		bg: '#ede9fe',
	},
	{
		name: 'Amy Powell',
		initials: 'AP',
		role: 'Care Staff',
		lastActive: 'Yesterday',
		status: 'Inactive',
		color: '#b45309',
		bg: '#fef3c7',
	},
];

const permissions = [
	'View resident profiles',
	'Add daily observations',
	'Generate care plans',
	'Access CQC reports',
];

const tableHeadStyle = {
	fontSize: '11px',
	fontWeight: 600,
	color: '#64748b',
	borderBottom: '1px solid #e2e8f0',
};

export default function UserManagement() {
	return (
		<MainGrid>
			<Box
				sx={{
					width: '100%',
				}}>
				{/* Header */}
				<Box sx={{ mb: 3 }}>
					<Typography
						sx={{
							fontSize: '22px',
							fontWeight: 600,
							color: dark,
							mb: 0.5,
						}}>
						User Management
					</Typography>

					<Typography
						sx={{
							color: '#64748b',
							fontSize: 14,
						}}>
						Manage admin, nursing, and care staff access to
						SenzCare.AI
					</Typography>
				</Box>

				<Grid container spacing={3}>
					{/* Team Members */}
					<Grid item xs={12} md={7}>
						<Card
							sx={{
								borderRadius: 4,
								boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
								border: '1px solid #e2e8f0',
							}}>
							<CardContent sx={{ p: 3 }}>
								<Stack
									direction="row"
									spacing={1.2}
									alignItems="center"
									mb={2.5}>
									<GroupAddOutlined
										sx={{
											fontSize: 16,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 600,
											color: '#0f172a',
										}}>
										Team Members
									</Typography>
								</Stack>

								<TableContainer component={Paper} elevation={0}>
									<Table>
										<TableHead>
											<TableRow>
												<TableCell sx={tableHeadStyle}>
													User
												</TableCell>

												<TableCell sx={tableHeadStyle}>
													Role
												</TableCell>

												<TableCell sx={tableHeadStyle}>
													Last Active
												</TableCell>

												<TableCell sx={tableHeadStyle}>
													Status
												</TableCell>

												<TableCell
													align="center"
													sx={tableHeadStyle}>
													Actions
												</TableCell>
											</TableRow>
										</TableHead>

										<TableBody>
											{users.map(user => (
												<TableRow key={user.name}>
													<TableCell>
														<Stack
															direction="row"
															spacing={1.5}
															alignItems="center">
															<Avatar
																sx={{
																	width: 36,
																	height: 36,
																	fontSize: 13,
																	fontWeight: 700,
																	bgcolor:
																		user.bg,
																	color: user.color,
																}}>
																{user.initials}
															</Avatar>

															<Typography
																fontWeight={400}
																fontSize={13}>
																{user.name}
															</Typography>
														</Stack>
													</TableCell>

													<TableCell
														sx={{
															fontSize: 13,
															color: '#334155',
														}}>
														{user.role}
													</TableCell>

													<TableCell
														sx={{
															fontSize: 13,
															color: '#64748b',
														}}>
														{user.lastActive}
													</TableCell>

													<TableCell>
														<Chip
															label={user.status}
															size="small"
															sx={{
																fontWeight: 600,
																fontSize: 11,
																backgroundColor:
																	user.status ===
																	'Active'
																		? '#dcfce7'
																		: '#e2e8f0',
																color:
																	user.status ===
																	'Active'
																		? '#166534'
																		: '#475569',
															}}
														/>
													</TableCell>

													<TableCell align="center">
														<Button
															size="small"
															variant="outlined"
															sx={{
																minWidth: 36,
																width: 36,
																height: 36,
																borderRadius: 2,
																borderColor:
																	'#cbd5e1',
															}}>
															<EditOutlinedIcon
																sx={{
																	fontSize: 18,
																	color: '#1a2424',
																}}
															/>
														</Button>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</CardContent>
						</Card>
					</Grid>

					{/* Invite Team Member */}
					<Grid item xs={12} md={5}>
						<Card
							sx={{
								borderRadius: 4,
								boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
								border: '1px solid #e2e8f0',
							}}>
							<CardContent sx={{ p: 3 }}>
								<Stack
									direction="row"
									spacing={1.2}
									alignItems="center"
									mb={2.5}>
									<PersonAddOutlined
										sx={{
											fontSize: 16,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 600,
											color: '#0f172a',
										}}>
										Invite Team Member
									</Typography>
								</Stack>

								<Stack spacing={2.5}>
									<Box>
										<Typography
											sx={{
												fontSize: 12,
												fontWeight: 500,
												mb: 1,
												color: '#6b8888',
											}}>
											Full Name
										</Typography>

										<TextField
											fullWidth
											size="small"
											placeholder="Full name"
											sx={{
												'& .MuiInputBase-input': {
													fontSize: 13,
												},
											}}
										/>
									</Box>

									<Box>
										<Typography
											sx={{
												fontSize: 12,
												fontWeight: 500,
												mb: 1,
												color: '#6b8888',
											}}>
											Work Email
										</Typography>

										<TextField
											fullWidth
											size="small"
											placeholder="name@careorg.co.uk"
											sx={{
												'& .MuiInputBase-input': {
													fontSize: 13,
												},
											}}
										/>
									</Box>

									<Box>
										<Typography
											sx={{
												fontSize: 12,
												fontWeight: 500,
												mb: 1,
												color: '#6b8888',
											}}>
											Role
										</Typography>

										<TextField
											select
											fullWidth
											size="small"
											defaultValue="Care Staff"
											sx={{
												'& .MuiInputBase-input': {
													fontSize: 13,
												},
											}}>
											<MenuItem value="Care Staff">
												Care Staff
											</MenuItem>

											<MenuItem value="Senior Nurse">
												Senior Nurse
											</MenuItem>

											<MenuItem value="Care Home Admin">
												Care Home Admin
											</MenuItem>
										</TextField>
									</Box>

									<Box>
										<Typography
											sx={{
												fontSize: 13,
												fontWeight: 600,
												mb: 1,
											}}>
											Permissions
										</Typography>

										<Stack spacing={0.5}>
											{permissions.map(
												(permission, index) => (
													<Stack
														key={permission}
														direction="row"
														alignItems="center"
														spacing={1}>
														<Checkbox
															defaultChecked={
																index < 2
															}
															size="small"
															sx={{
																color: '#0f766e',
																'&.Mui-checked':
																	{
																		color: '#0f766e',
																	},
															}}
														/>

														<Typography
															fontSize={13}>
															{permission}
														</Typography>
													</Stack>
												),
											)}
										</Stack>
									</Box>
									<Button
										fullWidth
										variant="contained"
										startIcon={<SendRoundedIcon />}
										sx={{
											borderRadius: '12px',
											backgroundColor: teal,
											textTransform: 'none',
											fontWeight: 600,
											fontSize: '13px',
											boxShadow: 'none',
											'&:hover': {
												backgroundColor: teal,
												boxShadow: 'none',
											},
											color: dark,
										}}>
										Send Invite
									</Button>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>
		</MainGrid>
	);
}
